/**
 * ClassifyReportModal — 품목분류 검토보고서(미리보기 + PDF 다운로드 + 이메일).
 *   props: reportData(구조화 결과), onClose, reportId?(재발급 시 저장 report_id 재사용).
 *   서버(Spring /api/onto/report/classify/*)가 A4 보고서 HTML/PDF 생성.
 *   미리보기=iframe 격리(보고서 @page CSS 누출 방지). 이메일 수신자=본인 디폴트·편집 가능.
 *   발급(PDF/이메일) 성공 시 이력 자동 저장(보고서당 1건, report_id dedup) — [[reportHistoryApi]].
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { newReportId, saveReport } from "./reportHistoryApi";

function token() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}
function authHeaders(json) {
  const h = {};
  const t = token();
  if (t) h.Authorization = `Bearer ${t}`;
  if (json) h["Content-Type"] = "application/json";
  return h;
}
// 로그인 사용자 이메일 추정(userId 가 이메일 형태면 사용).
function defaultEmail() {
  const uid = localStorage.getItem("userId") || sessionStorage.getItem("userId") || "";
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(uid)) return uid;
  return localStorage.getItem("userEmail") || "";
}

const BASE = "/api/onto/report/classify";

// 주요 FTA 코드→라벨(보고서 세율 요약용, ftaGroupMap 전체 대신 핵심만).
const MAJOR_FTA = [
  ["미국(한-미)", "FUS1"],
  ["EU(한-EU)", "FEU1"],
  ["중국(한-중)", "FCN1"],
  ["아세안(한-아세안)", "FAS1"],
  ["베트남(한-베)", "FVN1"],
];
const pct = (v) => (v == null ? null : `${v}%`);
// hsk-detail rates 코드맵 → 보고서 rates{basic,wto,fta,as_of}.
function buildRates(rates) {
  if (!rates || typeof rates !== "object") return null;
  const fta = {};
  for (const [label, code] of MAJOR_FTA) {
    if (rates[code] != null) fta[label] = pct(rates[code]);
  }
  const out = {
    basic: pct(rates.A),
    wto: pct(rates.W1 != null ? rates.W1 : rates.W2),
    fta,
    as_of: new Date().toISOString().slice(0, 10),
  };
  return out.basic || out.wto || Object.keys(fta).length ? out : null;
}

export default function ClassifyReportModal({ reportData, onClose, reportId: reportIdProp }) {
  const [data, setData] = useState(reportData);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState(defaultEmail());
  const [toast, setToast] = useState(null);
  // 발급 세션 report_id — 재발급이면 저장 id 재사용(같은 이력 행 갱신), 아니면 새로 부여.
  const [reportId] = useState(() => reportIdProp || newReportId());

  // 발급 성공 시 이력 자동 저장(fire-and-forget). data=세율 병합된 스냅샷.
  const persist = (issuedVia, toEmail) =>
    saveReport({
      report_id: reportId,
      product_name: data.product_name,
      decided_hsk: data.decided_hsk,
      report: data,
      issued_via: issuedVia,
      to: toEmail,
    });

  // 결정세번(10자리)의 세율 조회 → 보고서에 병합(세율 섹션 채움·시점 고정).
  useEffect(() => {
    const digits = String(reportData.decided_hsk || "").replace(/\D/g, "");
    if (reportData.rates || digits.length < 10) return; // 이미 있거나 10자리 아니면 skip
    let alive = true;
    fetch(`/api/onto/hsk-detail?hsk=${digits.slice(0, 10)}&lang=ko`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        const rates = buildRates(d && d.rates);
        if (alive && rates) setData((prev) => ({ ...prev, rates }));
      })
      .catch(() => {}); // 세율 실패해도 보고서는 생성(섹션만 생략)
    return () => {
      alive = false;
    };
  }, [reportData]);

  // 미리보기 로드 — data(세율 병합본) 변경 시 재생성
  useEffect(() => {
    let alive = true;
    setPreviewErr(null);
    setPreviewHtml(null);
    fetch(`${BASE}/preview`, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(data),
    })
      .then((r) => (r.ok ? r.text() : r.json().then((j) => Promise.reject(new Error(j.error || "HTTP " + r.status)))))
      .then((html) => alive && setPreviewHtml(html))
      .catch((e) => alive && setPreviewErr(String(e.message || e)));
    return () => {
      alive = false;
    };
  }, [data]);

  const flash = (m) => {
    setToast(m);
    setTimeout(() => setToast(null), 3500);
  };

  const download = () => {
    setBusy(true);
    fetch(`${BASE}/pdf`, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify(data),
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("HTTP " + r.status))))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `검토보고서_${String(data.decided_hsk || "HS").replace(/\D/g, "")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        persist("pdf", null); // 다운로드 성공 → 이력 자동 저장
      })
      .catch((e) => flash("PDF 다운로드 실패: " + e.message))
      .finally(() => setBusy(false));
  };

  const sendEmail = () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) {
      flash("유효한 수신자 이메일을 입력하세요.");
      return;
    }
    setBusy(true);
    fetch(`${BASE}/email`, {
      method: "POST",
      headers: authHeaders(true),
      body: JSON.stringify({ ...data, to: to.trim() }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (ok && j.ok) {
          flash(`${to.trim()} 로 발송했습니다${j.attached ? " (PDF 첨부)" : " (본문)"}`);
          persist("email", to.trim()); // 발송 성공 → 이력 자동 저장
        } else flash("발송 실패: " + (j.message || j.error || "오류"));
      })
      .catch((e) => flash("발송 실패: " + e.message))
      .finally(() => setBusy(false));
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1200,
        padding: "32px 20px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--audit-bg, #fff)",
          border: "1px solid var(--audit-border, #e5e7eb)",
          borderRadius: 10,
          width: "100%",
          maxWidth: 820,
          maxHeight: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          color: "var(--audit-text, #111)",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid var(--audit-border, #e5e7eb)",
          }}
        >
          <b style={{ fontSize: 14 }}>품목분류 검토보고서</b>
          <button
            onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "var(--audit-text-muted)" }}
            title="닫기"
          >
            ✕
          </button>
        </div>

        {/* 미리보기 */}
        <div style={{ flex: 1, overflow: "auto", background: "#f3f4f6", padding: 12, minHeight: 200 }}>
          {previewErr ? (
            <div style={{ padding: 20, fontSize: 13, color: "#b91c1c" }}>
              미리보기 실패 — {previewErr}
            </div>
          ) : previewHtml == null ? (
            <div style={{ padding: 20, fontSize: 13, color: "var(--audit-text-muted)" }}>
              보고서 생성 중…
            </div>
          ) : (
            <iframe
              title="검토보고서 미리보기"
              srcDoc={previewHtml}
              style={{
                width: "100%",
                height: 520,
                border: "1px solid #ddd",
                borderRadius: 4,
                background: "#fff",
              }}
            />
          )}
        </div>

        {/* 액션바 */}
        <div
          style={{
            borderTop: "1px solid var(--audit-border, #e5e7eb)",
            padding: "10px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "var(--audit-text-muted)", flexShrink: 0 }}>이메일</span>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="수신자 이메일 (본인 기본, 변경 가능)"
              style={{
                flex: 1,
                minWidth: 180,
                padding: "6px 10px",
                fontSize: 13,
                border: "1px solid var(--audit-border-strong, #cdd1d8)",
                borderRadius: 6,
                background: "var(--audit-bg, #fff)",
                color: "var(--audit-text, #111)",
                fontFamily: "inherit",
              }}
            />
            <button
              onClick={sendEmail}
              disabled={busy || !previewHtml}
              className="audit-btn audit-btn-primary"
              style={{ opacity: busy || !previewHtml ? 0.6 : 1, whiteSpace: "nowrap" }}
            >
              이메일 전송
            </button>
            <button
              onClick={download}
              disabled={busy || !previewHtml}
              className="audit-btn"
              style={{ opacity: busy || !previewHtml ? 0.6 : 1, whiteSpace: "nowrap" }}
            >
              PDF 다운로드
            </button>
          </div>
          {toast && (
            <div style={{ fontSize: 12.5, color: "var(--audit-accent, #2563eb)" }}>{toast}</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
